/**
 * Reaching the tool: fetching what it can run, and running it.
 *
 * The half of `experiments.ts` that touches real machinery. Kept apart
 * because it drags two heavy things behind it - a module's intent catalogue,
 * and through the panel registry every panel in the fab - and the brief, the
 * wire and the check harness need none of that to say what a technician can
 * run.
 *
 * NOTHING NEW DECIDES ANYTHING HERE. The tool's assistant already turns a
 * question into a routing that "names one of the REGISTERED intents and
 * carries only params those intents declared", executes it against the real
 * solver, and returns a report assembled from measured output. This module is
 * only the seam that lets a conversation reach that machinery: it finds the
 * panel, takes the assistant the panel now offers (`assistant` in
 * tools/types.ts), and hands the question over. The model does not write
 * physics here any more than it does in the panel - it decides only that an
 * experiment is what the student is asking for.
 *
 * ONE ASSISTANT PER TOOL, SHARED. `loadToolPanel` caches the built panel for
 * the session, so an experiment run from a conversation is the same
 * assistant's history the panel's inquiry pane shows. A student who asks
 * Sarah and then walks into ETCH-01 finds the run they started already in the
 * transcript, which is the truth of it: there is one tool and one record.
 *
 * THE MENU IS FETCHED SEPARATELY FROM THE ASSISTANT, on purpose. The brief is
 * built synchronously on every line and needs to say WHAT can be run; the
 * assistant needs the panel's store, whose constructor stands up (and on some
 * tools solves) a simulation. So the intent catalogue - a plain list of ids,
 * examples and summaries - is fetched on its own when the conversation opens,
 * and the store is built only when a question actually calls for a run. Same
 * split, and for the same reason, as `preloadToolPanel` warming a chunk
 * without building the panel behind it.
 */

import type { AskProgress, Transcript } from '../assistant/Assistant'
import { getTool } from '../fab/layout'
import { panelStore } from '../app/panel/panelStore'
import { loadToolPanel } from '../tools/registry'
import { hasExperiments, menuKnown, rememberMenu, type ExperimentMenuItem } from './experiments'

/**
 * An intent list seen as a menu. Structural on purpose: `ETCH_INTENTS` is
 * `Intent<EtchIntentContext>[]` and `LITHO_INTENTS` is something else again,
 * and this file has no business naming either - it reads three strings.
 */
type ExperimentMenu = ReadonlyArray<ExperimentMenuItem>

/** Which catalogue answers for which tool. Keys mirror EXPERIMENT_TOOLS. */
const CATALOGUES: Record<string, () => Promise<ExperimentMenu>> = {
  etch: () => import('../assistant/intents').then((m) => m.ETCH_INTENTS),
  cmp: () => import('../assistant/cmpIntents').then((m) => m.CMP_INTENTS),
  litho: () => import('../assistant/lithoIntents').then((m) => m.LITHO_INTENTS),
  thermal: () => import('../assistant/thermalIntents').then((m) => m.THERMAL_INTENTS),
  implant: () => import('../assistant/tools/implant').then((m) => m.IMPLANT_INTENTS),
  deposition: () => import('../assistant/depIntents').then((m) => m.DEP_INTENTS),
  metrology: () => import('../assistant/metroIntents').then((m) => m.METRO_INTENTS),
  epi: () => import('../assistant/tools/epi').then((m) => m.EPI_INTENTS),
  probe: () => import('../assistant/tools/probe').then((m) => m.PROBE_INTENTS),
  euv: () => import('../assistant/tools/euv').then((m) => m.EUV_INTENTS),
  ebeam: () => import('../assistant/tools/ebeam').then((m) => m.SEM_INTENTS),
  quantum: () => import('../assistant/tools/quantum').then((m) => m.CRYO_INTENTS),
}

const inFlight = new Set<string>()

/**
 * Fetch this tool's catalogue, once, in the background.
 *
 * Called when a conversation opens, which buys the whole time the student
 * spends typing their first line.
 */
export function warmExperiments(toolId: string | null): void {
  if (!toolId || !hasExperiments(toolId) || menuKnown(toolId) || inFlight.has(toolId)) return
  inFlight.add(toolId)
  void CATALOGUES[toolId]()
    .then((intents) => {
      // English, both fields, and neither is a translation bug. The example
      // is what the local router's patterns match, so translating it would
      // stop it routing; the summary is instruction to a model rather than
      // text a student reads, the same argument cast.ts makes for keeping
      // the personas out of the catalogues. The technician answers in the
      // student's language regardless.
      rememberMenu(
        toolId,
        intents.map((i) => ({ id: i.id, example: i.example, summary: i.summary })),
      )
    })
    .catch(() => {
      // A catalogue that did not arrive is a technician who does not offer
      // to run anything this conversation. Nothing to tell the student.
    })
    .finally(() => inFlight.delete(toolId))
}

/**
 * Run it. Builds the tool's panel store on first ask - the one main-thread
 * cost in this file, and the reason `registry.ts` keeps that constructor off
 * the walk - then hands the question to the assistant.
 *
 * Null when there is no tool, no panel services yet, or a panel that does
 * not offer its assistant: the caller then says nothing was run rather than
 * inventing a result.
 */
export async function runExperiment(
  toolId: string,
  question: string,
  onProgress?: (p: AskProgress) => void,
): Promise<Transcript | null> {
  const tool = getTool(toolId)
  const services = panelStore.services
  if (!tool || !services) return null
  const built = await loadToolPanel(toolId, services, tool)
  const assistant = built.def.assistant?.(built.store)
  if (!assistant) return null
  return assistant.ask(question, onProgress)
}
